<template>
  <pdl-collapse-item :name="name">
    <div slot="title" class="heading heading--sm" qaid="warehouse.heading">
      {{ warehouse.name }} ({{ warehouse.totalQuantity }})
    </div>

    <slot name="info">
      <div class="grid-x">
        <div v-cloak class="cell large-4" qaid="warehouse.infoText">
          <p>{{ infoText }}</p>
        </div>
      </div>
    </slot>

    <grid-container>
      <table class="b2b-grid b2b-grid--collapse is-compact" qaid="allocated-items-sku-grid">
        <thead class="b2b-grid__header" qaid="warehouse.grid.head">
          <th class="b2b-grid__cell b2b-grid__head" qaid="warehouse.grid.checkBox">
            <label class="checkbox mb-0">
              <input
                id="selectAllItems"
                v-model="allSelectedValue"
                type="checkbox"
                :disabled="isOnHold"
                @change="selectAllItems"
              />
              <span class="control-indicator"></span>
            </label>
          </th>
          <th
            v-for="header in getHeaders()"
            :key="header.label"
            class="b2b-grid__cell b2b-grid__head"
            :qaid="qaLabel(header.label)"
          >
            {{ header.label }}
          </th>
        </thead>

        <tbody qaid="warehouse.grid.body">
          <template v-for="item in paginatedData">
            <warehouse-item
              :key="item.id"
              :cart-summary="cartSummary"
              :item="item"
              :warehouse="warehouse"
              :headers="headers"
              :base-properties="baseProperties"
              @update-allocated-items="updateAllocatedItems"
            />
          </template>
        </tbody>
      </table>
    </grid-container>
    <pagination
      v-if="pageCount > 1"
      ref="pagination"
      :page-number="pageNumber"
      :page-count="pageCount"
      @page-change="pageChange"
      @prev-page="prevPage"
      @next-page="nextPage"
    >
    </pagination>
  </pdl-collapse-item>
</template>

<script>
import axios from 'axios';
import every from 'lodash/every';
import isEmpty from 'lodash/isEmpty';
import pagination from '@/mixins/pagination';
import {mapGetters, mapMutations} from 'vuex';
import {PdlCollapseItem} from '@pedal/pdl-collapse';
import GridContainer from '@/components/grid/GridContainer';
import WarehouseItem from '@/components/containers/checkout/WarehouseItem';
import Pagination from '@/components/Pagination';
import warehouse from '@/mixins/warehouse';

export default {
  components: {PdlCollapseItem, GridContainer, WarehouseItem, Pagination},

  mixins: [pagination, warehouse],

  props: {
    size: {
      type: Number,
      required: false,
      default: ACC.allocatedItemsPageSize || 6,
    },
    cartData: {
      type: Object,
      default: () => {},
    },
    cartSummary: {
      type: Object,
      default: () => {},
    },
    warehouse: {
      type: Object,
      default: () => {},
    },
    type: {
      type: String,
      default: '',
    },
    index: {
      type: Number,
      default: 0,
    },
    headers: {
      type: Array,
      default: () => [],
    },
    infoText: {
      type: String,
      default: '',
    },
    isOnHold: Boolean,
    baseProperties: {
      type: Array,
      default: () => [],
      required: false,
    },
  },

  data() {
    return {
      allSelectedValue: this.allSelected,
      listData: this.warehouse.allocatedItems,
      updateItemCallCount: 0,
    };
  },

  computed: {
    allSelected() {
      return every(this.warehouse.allocatedItems, 'selected');
    },

    isAllSelectable() {
      return every(this.warehouse.allocatedItems, 'isAllowedToShip');
    },

    name() {
      return `${this.type}-${this.warehouse.id}`;
    },

    allSelectedItems() {
      return this.getSelectedItems();
    },
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },

  watch: {
    allSelected() {
      this.allSelectedValue = this.allSelected;
      return this.allSelectedValue;
    },
  },

  created() {
    this.allSelectedValue = this.allSelected;
  },

  methods: {
    updateAllocatedItems() {
      let itemsToUpdate = this.fillSelectedAllocatedItems(this.cartData);
      if (!isEmpty(itemsToUpdate)) {
        this.updateItems(itemsToUpdate);
      }
    },

    updateItems(items) {
      // Send the API call
      this.updateItemCallCount++;
      this.setHasAllocatedItemsProcessing(true);
      axios
        .put('/checkout-b2b/api/allocated-items/update-items', items)
        .then(({data}) => {
          this.updateItemCallCount--;
          if (this.updateItemCallCount === 0) {
            this.setHasAllocatedItemsProcessing(false);
          }
          this.$emit('update-cart-summary', {...data});
        })
        .catch((error) => console.log(error));
    },

    selectAllItems() {
      const newState = this.allSelectedValue ? true : false;

      // Set the selected property on every item, and add each to the payload that will be sent to the API.
      this.warehouse.allocatedItems.forEach((item) => {
        if (item.isAllowedToShip && !this.warehouse.isOnHold) {
          item.selected = newState;
          return item;
        }
      });

      this.updateAllocatedItems();

      if (isEmpty(this.fillSelectedAllocatedItems(this.cartData))) {
        this.allSelectedValue = false;
      }

      return this.warehouse.allocatedItems;
    },

    getSelectedItems() {
      return this.warehouse.allocatedItems.filter((item) => {
        return item.selected === true;
      });
    },

    qaLabel(label) {
      let prefix = 'warehouse.grid.column';
      let strippedLabel = label ? '.' + label.replace(/[^0-9a-z]/gi, '') : '';

      return prefix + strippedLabel;
    },

    getHeaders() {
      return this.headers.filter((header) => {
        if (this.consumerFriendlyMode && (header.label == 'Price' || header.label == 'Subtotal')) {
          return false;
        } else {
          return true;
        }
      });
    },
    ...mapMutations('cart', ['setHasAllocatedItemsProcessing']),
  },
};
</script>
